/* ==========================================================================
   Admin Categories Page
   PORTAL-77: Dedicated section for managing product categories — add,
   rename, delete — independent of products. Assignment to products happens
   in the product form (multi-select).
   ========================================================================== */

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Edit2, Trash2, MoreVertical, Package, RefreshCw, Tags } from 'lucide-react'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Alert } from '@/components/ui/Alert'
import { FormField } from '@/components/ui/FormField'
import { CategoryProductsModal } from '@/components/admin/CategoryProductsModal'
import { AdminTableSkeleton } from '@/components/skeletons'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import {
  listCategories,
  createCategory,
  renameCategory,
  deleteCategory,
} from '@/api/admin'
import { ApiClientError } from '@/api/client'
import type { Category } from '@/api/types'

/* --------------------------------------------------------------------------
   Empty State
   -------------------------------------------------------------------------- */

function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="mx-auto w-16 h-16 bg-soft-lavender rounded-full flex items-center justify-center mb-4">
        <Tags className="w-8 h-8 text-deep-purple" />
      </div>
      <h3 className="text-lg font-semibold text-charcoal mb-2">
        No categories yet
      </h3>
      <p className="text-gray-600 max-w-sm mx-auto">
        Create categories to group your products. Providers see the catalog
        organized by category, and a product can belong to several categories.
      </p>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Main Component
   -------------------------------------------------------------------------- */

export function Categories() {
  const { pharmacy } = useAdminAuth()
  const { success: showSuccess, error: showError } = useToast()
  const slug = pharmacy?.portal_slug || ''

  // Data state
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Add form state
  const [newName, setNewName] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  // Rename modal state
  const [renaming, setRenaming] = useState<Category | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [isRenaming, setIsRenaming] = useState(false)

  // Delete confirmation state
  const [deleting, setDeleting] = useState<Category | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Manage-products modal state
  const [managing, setManaging] = useState<Category | null>(null)

  const fetchCategories = useCallback(async () => {
    if (!slug) return

    setLoading(true)
    setError(null)

    try {
      const response = await listCategories(slug)
      setCategories(response.categories)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to load categories. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  async function handleAdd(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const name = newName.trim()
    if (!name) return

    setIsAdding(true)
    try {
      await createCategory(slug, name)
      showSuccess(`Category "${name}" created`)
      setNewName('')
      await fetchCategories()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to create category. Please try again.'
      showError(message)
    } finally {
      setIsAdding(false)
    }
  }

  function handleRenameClick(category: Category) {
    setRenaming(category)
    setRenameValue(category.name)
  }

  async function handleConfirmRename(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!renaming) return
    const name = renameValue.trim()
    if (!name || name === renaming.name) {
      setRenaming(null)
      return
    }

    setIsRenaming(true)
    try {
      await renameCategory(slug, renaming.id, name)
      showSuccess('Category renamed')
      setRenaming(null)
      await fetchCategories()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to rename category. Please try again.'
      showError(message)
    } finally {
      setIsRenaming(false)
    }
  }

  async function handleConfirmDelete() {
    if (!deleting) return

    setIsDeleting(true)
    try {
      await deleteCategory(slug, deleting.id)
      showSuccess('Category deleted')
      setDeleting(null)
      await fetchCategories()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to delete category. Please try again.'
      showError(message)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-4xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal">Categories</h1>
            <p className="text-charcoal/70 mt-1">
              Group your catalog products. Assign categories to products from
              the Product Catalog page.
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchCategories}
              className="ml-4"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </Alert>
        )}

        {/* Add Category */}
        <Card className="p-4 sm:p-6 mb-6">
          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                aria-label="New category name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New category name, e.g., Hormone Therapy"
                maxLength={100}
                disabled={isAdding}
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              loading={isAdding}
              disabled={!newName.trim()}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </form>
        </Card>

        {/* Main Content */}
        <Card className="overflow-visible">
          {loading ? (
            <AdminTableSkeleton columns={3} rows={4} />
          ) : categories.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden sm:block">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left text-sm font-medium text-gray-600 pb-3">
                        Name
                      </th>
                      <th className="text-left text-sm font-medium text-gray-600 pb-3">
                        Products
                      </th>
                      <th className="text-right text-sm font-medium text-gray-600 pb-3">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <tr
                        key={category.id}
                        className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 pr-4">
                          <Link
                            to={`/rx/${slug}/admin/catalog?category=${category.id}`}
                            className="text-charcoal font-medium hover:text-deep-purple hover:underline transition-colors"
                            title={`View products in ${category.name}`}
                          >
                            {category.name}
                          </Link>
                        </td>
                        <td className="py-4 pr-4">
                          <Link
                            to={`/rx/${slug}/admin/catalog?category=${category.id}`}
                            className="text-gray-600 hover:text-deep-purple hover:underline transition-colors"
                          >
                            {category.product_count}
                          </Link>
                        </td>
                        <td className="py-4 text-right">
                          <Menu as="div" className="relative inline-block text-left">
                            <MenuButton
                              aria-label={`Actions for ${category.name}`}
                              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
                            >
                              <MoreVertical className="w-4 h-4 text-gray-500" />
                            </MenuButton>
                            <MenuItems
                              anchor="bottom end"
                              className="z-50 w-44 [--anchor-gap:4px] bg-white rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none"
                            >
                              <div className="py-1">
                                <MenuItem>
                                  {({ active }) => (
                                    <button
                                      onClick={() => setManaging(category)}
                                      className={`flex items-center w-full px-4 py-2 text-sm ${
                                        active ? 'bg-gray-100' : ''
                                      } text-charcoal`}
                                    >
                                      <Package className="w-4 h-4 mr-2" />
                                      Manage Products
                                    </button>
                                  )}
                                </MenuItem>
                                <MenuItem>
                                  {({ active }) => (
                                    <button
                                      onClick={() => handleRenameClick(category)}
                                      className={`flex items-center w-full px-4 py-2 text-sm ${
                                        active ? 'bg-gray-100' : ''
                                      } text-charcoal`}
                                    >
                                      <Edit2 className="w-4 h-4 mr-2" />
                                      Rename
                                    </button>
                                  )}
                                </MenuItem>
                                <MenuItem>
                                  {({ active }) => (
                                    <button
                                      onClick={() => setDeleting(category)}
                                      className={`flex items-center w-full px-4 py-2 text-sm ${
                                        active ? 'bg-red-50' : ''
                                      } text-error`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </button>
                                  )}
                                </MenuItem>
                              </div>
                            </MenuItems>
                          </Menu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="sm:hidden space-y-4">
                {categories.map((category) => (
                  <div
                    key={category.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <Link
                        to={`/rx/${slug}/admin/catalog?category=${category.id}`}
                        className="font-medium text-charcoal hover:text-deep-purple hover:underline transition-colors"
                      >
                        {category.name}
                      </Link>
                      <Link
                        to={`/rx/${slug}/admin/catalog?category=${category.id}`}
                        className="text-sm text-gray-500 whitespace-nowrap hover:text-deep-purple"
                      >
                        {category.product_count}{' '}
                        {category.product_count === 1 ? 'product' : 'products'}
                      </Link>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setManaging(category)}
                      >
                        <Package className="w-3.5 h-3.5 mr-1" />
                        Products
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRenameClick(category)}
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-1" />
                        Rename
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleting(category)}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Manage Products Modal */}
      <CategoryProductsModal
        open={managing !== null}
        onClose={() => setManaging(null)}
        category={managing}
        slug={slug}
        onSaved={(changedCount) => {
          showSuccess(
            changedCount === 0
              ? 'No changes to save'
              : `Updated ${changedCount} ${changedCount === 1 ? 'product' : 'products'}`
          )
          fetchCategories()
        }}
        onError={showError}
      />

      {/* Rename Modal */}
      <Modal
        open={renaming !== null}
        onClose={() => setRenaming(null)}
        title="Rename Category"
        size="sm"
      >
        <form onSubmit={handleConfirmRename} className="space-y-4">
          <FormField label="Category Name" required htmlFor="rename-category">
            <Input
              id="rename-category"
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              maxLength={100}
              disabled={isRenaming}
              autoFocus
            />
          </FormField>
          <p className="text-sm text-gray-600">
            Products in this category keep it under the new name.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setRenaming(null)}
              disabled={isRenaming}
              className="sm:order-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isRenaming}
              disabled={!renameValue.trim()}
              className="sm:order-2 sm:ml-auto"
            >
              Save
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Delete Category"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete{' '}
          <strong className="text-charcoal">{deleting?.name}</strong>?
          {deleting && deleting.product_count > 0 && (
            <>
              {' '}
              {deleting.product_count}{' '}
              {deleting.product_count === 1 ? 'product' : 'products'} will lose
              this category but remain in the catalog.
            </>
          )}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="secondary"
            onClick={() => setDeleting(null)}
            disabled={isDeleting}
            className="sm:order-1"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmDelete}
            loading={isDeleting}
            className="sm:order-2 sm:ml-auto"
          >
            Delete Category
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default Categories
